import { useMemo, type ReactNode } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { RavenThemeCssVars } from "@ravenappdev/raven-ui";
import { buildEssTheme } from ".";
import ColorSchemeSync from "./ColorSchemeSync";

/**
 * Pins a subtree to the light theme, whatever the user's appearance preference
 * is. Two surfaces need it, both for the same reason: their paint is not the
 * app palette, so a dark palette leaves them half-converted.
 *
 * - **The print routes** (`/suggestions/:id/print`, `/overview/print`). A
 *   printed page is ink on paper; dark mode is a screen preference. `index.css`
 *   paints `.print-root` white regardless, so a dark MUI theme would leave
 *   dark-lifted Alerts and near-white body text sitting on a white sheet.
 *   `@media print` alone would not fix it either — these routes are read on
 *   screen as a preview before anyone hits Print.
 * - **The paper-palette screens**: `Login` (wrapped in `Routes.tsx`) and
 *   `NoRoleAssigned` (the signed-in-but-no-role gate, wrapped in
 *   `RequireAuth.tsx`). They run their own paper/serif palette out of
 *   `constants/authTokens.ts`, which the app theme does not model and which has no dark counterpart; only the MUI
 *   controls inside them (Select, Checkbox, Alert) read the theme, so a dark
 *   palette shows up as dark fields on a parchment card. Inventing a second
 *   dark palette for a surface the user sees for ten seconds buys nothing.
 *
 * The nested `CssBaseline` is deliberate. It re-emits the `body` rules from the
 * light palette and, mounting after the app-root one, wins on insertion order —
 * so the page around the card is parchment, not the dark canvas. The nested
 * `ColorSchemeSync` does the same for `<html>` (`data-ess-mode`,
 * `color-scheme`, the `--ess-*` vars) and hands them back on unmount.
 */
export default function ForceLightTheme({ children }: { children: ReactNode }) {
  const light = useMemo(() => buildEssTheme("light"), []);
  return (
    <ThemeProvider theme={light}>
      <RavenThemeCssVars />
      <CssBaseline />
      <ColorSchemeSync />
      {children}
    </ThemeProvider>
  );
}
